// =============================================================================
// TASK-4705 — ONE people model for a job record (CRM opportunity + Project).
//
// THE PROBLEM THIS CLOSES. An opportunity carried TWO people lists in the same
// modal: `assignedPeople` ({personId, personName, role}) — the one that LOOKED
// like a permission list (it has a role column) but grants nothing — and
// `portal.assignees` ({personId, personName}) — the real grant
// (functions/src/services/crewPortalProjects.js crewAssigneeIds, read by
// getCrewPortalProjects / portalCompleteStep / portalSubmitNote). Measured on
// show-account 2026-09-10: one person on the card who got nothing, another
// holding the grant who appeared nowhere.
//
// THE MODEL. One list, one explicit capability:
//     people: [{ personId, personName, role, canCompleteSteps }]
// "Who is on this job" is the row; "may tick the tracker from a Driver PIN" is
// the capability. COD kept the two ideas apart on purpose; they now live on one
// row instead of in two lists.
//
// LEGACY FIELDS (kept until the migration is proven — row instruction):
//   * READ: normalizeOppPeople() migrates on read. If `people` is absent it is
//     built from assignedPeople ∪ portal.assignees. If it is present, the
//     stored grant (portal.assignees) still decides canCompleteSteps, and any
//     grant holder or assignedPeople row missing from `people` is added (an
//     old cached client may have written only the legacy lists) — so the screen can never
//     show a capability the server does not enforce, or hide someone who holds
//     one. Nothing in either legacy list is ever dropped on read.
//   * WRITE: legacyFieldsFromPeople() derives both legacy lists from `people`.
//     The server's grant field stays `portal.assignees`, now a mechanical
//     projection of people[].canCompleteSteps (no functions change, no new
//     grant path). Old cached clients that still read either field keep working.
//
// RENDERER RENDERS, HOST AUTHORIZES (CLAUDE.md Pitfall #8, PersonSections
// precedent). renderHTML() never decides who may edit or whether the capability
// is live — the host passes `canEdit`, `showCapability` and the action handler
// names in. Loaded as a side-effect module (window.ProjectPeople); never import
// a named export from it with a `?v=` buster (Pitfall #1).
// =============================================================================
(function () {
  'use strict';

  // COD r1 (Code Reviewer): NO silent truncation anywhere — cutting a list
  // could drop a grant holder on the next save. The cap only stops ADDING.
  var MAX_PEOPLE = 200;

  function str(v, max) {
    if (v === null || v === undefined) return '';
    var s = String(v);
    return s.length > max ? s.slice(0, max) : s;
  }

  function cleanRow(r, cap) {
    if (!r || typeof r !== 'object') return null;
    var personId = str(r.personId, 700);
    var personName = str(r.personName, 200);
    if (!personId && !personName) return null;
    return {
      personId: personId,
      personName: personName,
      role: str(r.role, 100),
      canCompleteSteps: cap === undefined ? r.canCompleteSteps === true : cap === true
    };
  }

  function keyOf(row) {
    return row.personId ? 'id:' + row.personId : 'name:' + row.personName.toLowerCase();
  }

  /**
   * The one people list for an opportunity, migrated on read.
   * @param {object} opp opportunity doc data (may be partial / legacy)
   * @returns {Array<{personId,personName,role,canCompleteSteps}>}
   */
  function normalizeOppPeople(opp) {
    var o = opp && typeof opp === 'object' ? opp : {};
    var portal = o.portal && typeof o.portal === 'object' ? o.portal : {};
    var grantRows = Array.isArray(portal.assignees) ? portal.assignees : null;
    // COD r1 (Code Reviewer): Sets / null-prototype maps, never `{}` — a
    // personId like "constructor" must not read as a grant.
    var grantIds = new Set();
    if (grantRows) {
      grantRows.forEach(function (a) { if (a && a.personId) grantIds.add(String(a.personId)); });
    }

    var out = [];
    var seen = new Map();
    function push(row) {
      if (!row) return;
      var k = keyOf(row);
      if (seen.has(k)) {
        // Merge: keep the first row, fill a missing name/role, OR the capability.
        var cur = out[seen.get(k)];
        if (!cur.personName && row.personName) cur.personName = row.personName;
        if (!cur.role && row.role) cur.role = row.role;
        cur.canCompleteSteps = cur.canCompleteSteps || row.canCompleteSteps;
        return;
      }
      seen.set(k, out.length);
      out.push(row);
    }

    if (Array.isArray(o.people)) {
      o.people.forEach(function (r) {
        var row = cleanRow(r);
        if (!row) return;
        // The STORED grant is the truth whenever there is one to read.
        if (grantRows) row.canCompleteSteps = !!(row.personId && grantIds.has(row.personId));
        push(row);
      });
    }
    // assignedPeople is ALWAYS merged in (COD r1): a new-client save derives it
    // from `people`, so this adds nothing then — but a person an old cached
    // client added after `people` existed is never lost.
    (Array.isArray(o.assignedPeople) ? o.assignedPeople : []).forEach(function (r) {
      var row = cleanRow(r, false);
      if (row && row.personId && grantIds.has(row.personId)) row.canCompleteSteps = true;
      push(row);
    });
    // Every grant holder is visible (the "holds the grant, appears nowhere" bug).
    if (grantRows) grantRows.forEach(function (a) { push(cleanRow(a, true)); });
    return out;
  }

  /**
   * The one people list for a Project doc. Projects had no people concept, so
   * there is no legacy shape to merge — just sanitize.
   */
  function normalizeProjectPeople(project) {
    var p = project && typeof project === 'object' ? project : {};
    var out = [];
    var seen = new Set();
    (Array.isArray(p.people) ? p.people : []).forEach(function (r) {
      var row = cleanRow(r);
      if (!row) return;
      var k = keyOf(row);
      if (seen.has(k)) return;
      seen.add(k);
      out.push(row);
    });
    return out;
  }

  /** Clean copy of a people list for a Firestore write. */
  function peopleForSave(people) {
    var out = [];
    var seen = new Set();
    (Array.isArray(people) ? people : []).forEach(function (r) {
      var row = cleanRow(r);
      if (!row) return;
      var k = keyOf(row);
      if (seen.has(k)) return;
      seen.add(k);
      out.push(row);
    });
    return out;
  }

  /**
   * Derive the two legacy fields from `people`. `portalAssignees` is the grant
   * the server reads: only rows with a personId AND canCompleteSteps.
   */
  function legacyFieldsFromPeople(people) {
    var clean = peopleForSave(people);
    return {
      assignedPeople: clean.map(function (r) {
        return { personId: r.personId, personName: r.personName, role: r.role };
      }),
      portalAssignees: clean.filter(function (r) { return r.personId && r.canCompleteSteps; })
        .map(function (r) { return { personId: r.personId, personName: r.personName }; })
    };
  }

  /** Same list (order-insensitive) — lets a host write `people` only on change. */
  function samePeople(a, b) {
    var x = peopleForSave(a), y = peopleForSave(b);
    if (x.length !== y.length) return false;
    var m = {};
    x.forEach(function (r) { m[keyOf(r)] = r; });
    return y.every(function (r) {
      var o = m[keyOf(r)];
      return !!o && o.personName === r.personName && o.role === r.role && o.canCompleteSteps === r.canCompleteSteps;
    });
  }

  /** Add a roster person; returns false when already on the list. */
  function addPerson(people, person) {
    var row = cleanRow(person, person && person.canCompleteSteps === true);
    if (!row) return false;
    var k = keyOf(row);
    if (people.some(function (r) { return keyOf(r) === k; })) return false;
    if (people.length >= MAX_PEOPLE) return false;
    people.push(row);
    return true;
  }

  function esc(s) {
    return String(s === null || s === undefined ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // A handler name is emitted into an inline attribute, so it must be a plain
  // dotted window path — never caller text.
  function handler(name) {
    return typeof name === 'string' && /^window(\.[A-Za-z_$][\w$]*)+$/.test(name) ? name : null;
  }

  /**
   * Render the list. The HOST decides everything access-shaped:
   *   opts.people          normalized list
   *   opts.roster          [{id, name, crewType?, role?, roles?}] for the add picker
   *   opts.idPrefix        DOM id prefix (unique per surface)
   *   opts.canEdit         host-computed; false = read-only rows, no picker
   *   opts.showCapability  host-computed; false hides the capability column
   *   opts.capabilityLabel / opts.capabilityHint  copy for the checkbox
   *   opts.onAdd / opts.onRemove / opts.onToggle  window.* handler names
   *   opts.emptyText
   */
  function renderHTML(opts) {
    var o = opts || {};
    var people = Array.isArray(o.people) ? o.people : [];
    var prefix = str(o.idPrefix || 'project-people', 60).replace(/[^\w-]/g, '');
    var canEdit = o.canEdit === true;
    var showCap = o.showCapability === true;
    var onAdd = handler(o.onAdd), onRemove = handler(o.onRemove), onToggle = handler(o.onToggle);
    var capLabel = esc(o.capabilityLabel || 'Can tick tracker steps');
    var html = '<div id="' + prefix + '-list" class="project-people-list">';
    if (!people.length) {
      html += '<div style="font-size:0.8125rem;color:var(--text-secondary);padding:4px 0;">' + esc(o.emptyText || 'Nobody on this job yet.') + '</div>';
    }
    people.forEach(function (p, i) {
      html += '<div class="opp-person-row" style="flex-wrap:wrap;">'
        + '<span class="material-icons" style="font-size:18px;color:var(--brand-blue);">person</span>'
        + '<span style="font-weight:500;flex:1;min-width:120px;">' + esc(p.personName || 'Unnamed') + '</span>'
        + (p.role ? '<span style="font-size:0.75rem;color:var(--text-secondary);">' + esc(p.role) + '</span>' : '');
      if (showCap) {
        if (canEdit && onToggle && p.personId) {
          html += '<label style="display:flex;align-items:center;gap:4px;font-size:0.75rem;color:var(--text-primary);cursor:pointer;white-space:nowrap;">'
            + '<input type="checkbox" ' + (p.canCompleteSteps ? 'checked ' : '') + 'onchange="' + onToggle + '(' + i + ', this.checked)"> ' + capLabel + '</label>';
        } else if (p.canCompleteSteps) {
          html += '<span style="font-size:0.75rem;color:var(--text-primary);white-space:nowrap;"><span class="material-icons" style="font-size:14px;vertical-align:middle;color:var(--brand-blue);">task_alt</span> ' + capLabel + '</span>';
        }
      }
      if (canEdit && onRemove) {
        html += '<button type="button" onclick="' + onRemove + '(' + i + ')" title="Remove" aria-label="Remove ' + esc(p.personName || 'person') + '" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);"><span class="material-icons" style="font-size:16px;">close</span></button>';
      }
      html += '</div>';
    });
    html += '</div>';
    if (showCap && o.capabilityHint) {
      html += '<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">' + esc(o.capabilityHint) + '</div>';
    }
    if (canEdit && onAdd) {
      var roster = Array.isArray(o.roster) ? o.roster : [];
      html += '<div style="display:flex;gap:8px;margin-top:8px;">'
        + '<select id="' + prefix + '-add" aria-label="Add a person" style="flex:1;min-width:0;padding:6px 10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8125rem;">'
        + '<option value="">Add a person...</option>'
        + roster.map(function (r) {
          var role = r.crewType || r.role || (Array.isArray(r.roles) ? r.roles[0] : '') || '';
          return '<option value="' + esc(r.id) + '" data-name="' + esc(r.name) + '" data-role="' + esc(role) + '">'
            + esc(r.name) + (r.crewType ? ' (' + esc(r.crewType) + ')' : '') + '</option>';
        }).join('')
        + '</select>'
        + '<button type="button" class="btn btn-secondary btn-sm" onclick="' + onAdd + '()" aria-label="Add person"><span class="material-icons" style="font-size:16px;">person_add</span></button>'
        + '</div>';
    }
    return html;
  }

  /** Read the picker the renderer drew; returns a person row or null. */
  function readPicker(idPrefix) {
    var prefix = str(idPrefix || 'project-people', 60).replace(/[^\w-]/g, '');
    var sel = typeof document !== 'undefined' ? document.getElementById(prefix + '-add') : null;
    if (!sel || !sel.value) return null;
    var opt = sel.options[sel.selectedIndex];
    var row = { personId: sel.value, personName: (opt && opt.dataset && opt.dataset.name) || '', role: (opt && opt.dataset && opt.dataset.role) || '', canCompleteSteps: false };
    sel.value = '';
    return row;
  }

  var api = {
    normalizeOppPeople: normalizeOppPeople,
    normalizeProjectPeople: normalizeProjectPeople,
    peopleForSave: peopleForSave,
    legacyFieldsFromPeople: legacyFieldsFromPeople,
    samePeople: samePeople,
    addPerson: addPerson,
    renderHTML: renderHTML,
    readPicker: readPicker
  };
  if (typeof window !== 'undefined') window.ProjectPeople = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
